import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ComingSoon from '../../components/common/ComingSoon';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  LinearProgress,
  Fade,
  Grow,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  BarChart as ChartIcon,
  PieChart as PieChartIcon,
  TableChart as TableIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as TrophyIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
} from 'chart.js';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';

// API hooks
import { useGetPresentationByIdQuery } from '../../services/api';

// Redux
import { useAppSelector } from '../../hooks/redux';
import { Question } from '../../services/slices/presentationSlice';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Chart colors
const chartColors = [
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
];

// Mock enhanced analytics data
const mockEnhancedAnalytics = {
  totalParticipants: 156,
  responseRate: 94.2,
  avgResponseTime: 2.3,
  completionRate: 89.7,
  topVoters: [
    { name: 'Sarah Chen', votes: 12, avatar: 'SC', responseTime: 1.8, accuracy: 95 },
    { name: 'Michael Rodriguez', votes: 11, avatar: 'MR', responseTime: 2.1, accuracy: 92 },
    { name: 'Emily Watson', votes: 10, avatar: 'EW', responseTime: 1.9, accuracy: 98 },
    { name: 'David Kim', votes: 9, avatar: 'DK', responseTime: 2.5, accuracy: 87 },
  ],
  sentimentAnalysis: {
    positive: 68,
    neutral: 25,
    negative: 7
  },
  engagementMetrics: {
    peakEngagement: '2:15 PM',
    dropoffRate: 5.8,
    averageSessionTime: '12.5 min'
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`results-tabpanel-${index}`}
      aria-labelledby={`results-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const VoteResults: React.FC = () => {
  const { presentationId } = useParams<{ presentationId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // State
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [chartType, setChartType] = useState(0);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  
  // Redux state
  const { user } = useAppSelector((state) => state.auth);
  
  // Check if user is admin
  const isAdmin = user?.attributes?.['custom:role'] === 'admin';
  
  // Fetch presentation
  const { 
    data: presentation, 
    isLoading: isLoadingPresentation, 
    error: presentationError 
  } = useGetPresentationByIdQuery(presentationId || '');
  
  // Fetch votes for each question
  const sortedQuestions = presentation?.questions ? [...presentation.questions].sort((a: Question, b: Question) => a.order - b.order) : [];
  const currentQuestion = sortedQuestions[selectedQuestionIndex];
  
  // Mock results data for impressive demo
  const mockResults = {
    optionCounts: [45, 32, 28, 18, 12],
    totalVotes: 135,
    demographics: {
      byRole: { 'Senior Partner': 25, 'Investment Director': 35, 'Principal': 40, 'Associate': 35 },
    }
  };
  
  if (isLoadingPresentation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  if (presentationError || !presentation) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          Error loading presentation. Please try again later.
        </Alert>
      </Container>
    );
  }
  
  if (!isAdmin) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning" sx={{ mt: 4 }}>
          You do not have permission to view voting results.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/presentations')}
          sx={{ mt: 2 }}
        >
          Back to Presentations
        </Button>
      </Container>
    );
  }
  
  const handleQuestionChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedQuestionIndex(newValue);
  };
  
  const handleChartTypeChange = (event: React.SyntheticEvent, newValue: number) => {
    setChartType(newValue);
  };
  
  // Prepare chart data
  const prepareChartData = () => {
    if (!currentQuestion) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Votes',
            data: [],
            backgroundColor: chartColors,
          },
        ],
      };
    }
    
    return {
      labels: currentQuestion.options,
      datasets: [
        {
          label: 'Votes',
          data: mockResults.optionCounts.slice(0, currentQuestion.options.length),
          backgroundColor: chartColors.slice(0, currentQuestion.options.length),
          borderColor: chartColors.map(color => color.replace('0.8', '1')),
          borderWidth: 2,
          borderRadius: 8,
        },
      ],
    };
  };

  const demographicsData = {
    labels: Object.keys(mockResults.demographics.byRole),
    datasets: [{
      data: Object.values(mockResults.demographics.byRole),
      backgroundColor: [
        alpha(theme.palette.primary.main, 0.8),
        alpha(theme.palette.secondary.main, 0.8),
        alpha(theme.palette.success.main, 0.8),
        alpha(theme.palette.warning.main, 0.8),
      ],
      borderWidth: 0,
    }],
  };

  const sentimentData = {
    labels: ['Positive', 'Neutral', 'Negative'],
    datasets: [{
      data: [mockEnhancedAnalytics.sentimentAnalysis.positive, mockEnhancedAnalytics.sentimentAnalysis.neutral, mockEnhancedAnalytics.sentimentAnalysis.negative],
      backgroundColor: [
        alpha(theme.palette.success.main, 0.8),
        alpha(theme.palette.info.main, 0.8),
        alpha(theme.palette.error.main, 0.8),
      ],
      borderWidth: 0,
    }],
  };

  const chartData = prepareChartData();
  
  // Chart options
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: currentQuestion?.text || 'Question Results',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        grid: {
          color: alpha(theme.palette.divider, 0.1),
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };
  
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
      },
      title: {
        display: true,
        text: currentQuestion?.text || 'Question Results',
      },
    },
  };

  const StatCard = ({ title, value, subtitle, icon, color, trend }: any) => (
    <Grow in timeout={1000}>
      <Card 
        sx={{ 
          height: '100%',
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 25px ${alpha(color, 0.3)}`,
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              backgroundColor: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {icon}
            </Box>
            {trend && (
              <Chip 
                label={`${trend > 0 ? '+' : ''}${trend}%`} 
                size="small" 
                color={trend > 0 ? "success" : "error"}
                icon={<TrendingUpIcon />}
              />
            )}
          </Box>
          <Typography variant="h3" fontWeight="bold" color={color} gutterBottom>
            {value}
          </Typography>
          <Typography variant="h6" color="text.primary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    </Grow>
  );
  
  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/presentations/${presentation.id}`)}
            sx={{ mb: 3 }}
          >
            Back to Presentation
          </Button>
          
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Voting Results Analytics
            </Typography>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {presentation.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Presenter: {presentation.presenter} • Date: {new Date(presentation.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
      </Fade>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Participants"
            value={mockEnhancedAnalytics.totalParticipants}
            subtitle="Unique voters"
            icon={<PeopleIcon sx={{ color: theme.palette.primary.main }} />}
            color={theme.palette.primary.main}
            trend={12.5}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Response Rate"
            value={`${mockEnhancedAnalytics.responseRate}%`}
            subtitle="Of invited participants"
            icon={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />}
            color={theme.palette.success.main}
            trend={8.3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Response Time"
            value={`${mockEnhancedAnalytics.avgResponseTime}s`}
            subtitle="Per question"
            icon={<TimerIcon sx={{ color: theme.palette.info.main }} />}
            color={theme.palette.info.main}
            trend={-15.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={`${mockEnhancedAnalytics.completionRate}%`}
            subtitle="Finished all questions"
            icon={<TrophyIcon sx={{ color: theme.palette.warning.main }} />}
            color={theme.palette.warning.main}
            trend={5.7}
          />
        </Grid>
      </Grid>

      {/* Export Actions */}
      <Fade in timeout={1200}>
        <Paper 
          sx={{ 
            p: 3, 
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsightsIcon color="primary" />
                Export & Share Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Download comprehensive reports and share insights with stakeholders
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  setComingSoonFeature('PDF Report Export');
                  setComingSoonOpen(true);
                }}
                sx={{ borderRadius: 3 }}
              >
                Export PDF Report
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  setComingSoonFeature('CSV Data Export');
                  setComingSoonOpen(true);
                }}
                sx={{ borderRadius: 3 }}
              >
                Export CSV Data
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  setComingSoonFeature('Executive Summary Export');
                  setComingSoonOpen(true);
                }}
                sx={{ borderRadius: 3 }}
              >
                Executive Summary
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>

      <Grid container spacing={4}>
        {/* Question Results */}
        <Grid item xs={12} lg={8}>
          <Fade in timeout={1400}>
            <Card>
              <CardContent>
                {/* Question Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs
                    value={selectedQuestionIndex}
                    onChange={handleQuestionChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="question tabs"
                  >
                    {sortedQuestions.map((question: Question, index: number) => (
                      <Tab 
                        key={question.id} 
                        label={`Question ${index + 1}`} 
                        id={`results-tab-${index}`}
                        aria-controls={`results-tabpanel-${index}`}
                      />
                    ))}
                  </Tabs>
                </Box>

                {/* Current Question Results */}
                {currentQuestion && (
                  <Box>
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      {currentQuestion.text}
                    </Typography>
                    
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                      <Tabs
                        value={chartType}
                        onChange={handleChartTypeChange}
                        aria-label="chart type tabs"
                      >
                        <Tab 
                          icon={<ChartIcon />} 
                          label="Bar Chart" 
                          id="chart-tab-0"
                          aria-controls="chart-tabpanel-0"
                        />
                        <Tab 
                          icon={<PieChartIcon />} 
                          label="Pie Chart" 
                          id="chart-tab-1"
                          aria-controls="chart-tabpanel-1"
                        />
                        <Tab 
                          icon={<TableIcon />} 
                          label="Detailed View" 
                          id="chart-tab-2"
                          aria-controls="chart-tabpanel-2"
                        />
                      </Tabs>
                    </Box>
                    
                    {/* Bar Chart */}
                    <TabPanel value={chartType} index={0}>
                      <Box sx={{ height: 400 }}>
                        <Bar options={barOptions} data={chartData} />
                      </Box>
                    </TabPanel>
                    
                    {/* Pie Chart */}
                    <TabPanel value={chartType} index={1}>
                      <Box sx={{ height: 400, display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ width: '70%', maxWidth: 500 }}>
                          <Pie options={pieOptions} data={chartData} />
                        </Box>
                      </Box>
                    </TabPanel>
                    
                    {/* Detailed Table View */}
                    <TabPanel value={chartType} index={2}>
                      <Grid container spacing={3}>
                        {currentQuestion.options.map((option: string, index: number) => (
                          <Grid item xs={12} key={index}>
                            <Paper sx={{ p: 3, border: `1px solid ${alpha(chartColors[index], 0.3)}` }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  {String.fromCharCode(65 + index)}. {option}
                                </Typography>
                                <Chip 
                                  label={`${mockResults.optionCounts[index] || 0} votes`}
                                  color="primary"
                                  variant="filled"
                                />
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={((mockResults.optionCounts[index] || 0) / mockResults.totalVotes) * 100}
                                sx={{ 
                                  height: 12, 
                                  borderRadius: 6,
                                  backgroundColor: alpha(chartColors[index], 0.2),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 6,
                                    backgroundColor: chartColors[index],
                                  }
                                }} 
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {Math.round(((mockResults.optionCounts[index] || 0) / mockResults.totalVotes) * 100)}% of total votes
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {mockResults.optionCounts[index] || 0} / {mockResults.totalVotes}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </TabPanel>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Analytics Sidebar */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            {/* Top Performers */}
            <Grid item xs={12}>
              <Grow in timeout={1600}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrophyIcon color="primary" />
                      Top Participants
                    </Typography>
                    <Box>
                      {mockEnhancedAnalytics.topVoters.map((voter, index) => (
                        <Box 
                          key={voter.name}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: index === 0 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.grey[500], 0.05),
                            border: index === 0 ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}` : 'none',
                          }}
                        >
                          <Avatar 
                            sx={{ 
                              bgcolor: index === 0 ? theme.palette.warning.main : theme.palette.primary.main,
                              fontWeight: 'bold'
                            }}
                          >
                            {voter.avatar}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {voter.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {voter.responseTime}s avg • {voter.accuracy}% accuracy
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${voter.votes}`} 
                            size="small" 
                            color={index === 0 ? "warning" : "primary"}
                            variant={index === 0 ? "filled" : "outlined"}
                          />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            {/* Demographics */}
            <Grid item xs={12}>
              <Grow in timeout={1800}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon color="primary" />
                      Demographics
                    </Typography>
                    <Box sx={{ height: 200, position: 'relative' }}>
                      <Doughnut 
                        data={demographicsData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }} 
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            {/* Sentiment Analysis */}
            <Grid item xs={12}>
              <Grow in timeout={2000}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InsightsIcon color="primary" />
                      Response Sentiment
                    </Typography>
                    <Box sx={{ height: 150, position: 'relative' }}>
                      <Doughnut 
                        data={sentimentData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }} 
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grid>
        </Grid>

        {/* Engagement Insights */}
        <Grid item xs={12}>
          <Fade in timeout={2400}>
            <Paper sx={{ p: 4, background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)` }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsightsIcon color="primary" />
                Key Insights & Recommendations
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {mockEnhancedAnalytics.engagementMetrics.peakEngagement}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Peak Engagement Time
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {mockEnhancedAnalytics.engagementMetrics.dropoffRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dropout Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {mockEnhancedAnalytics.engagementMetrics.averageSessionTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Session Duration
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        </Grid>
      </Grid>

      {/* Coming Soon Dialog */}
      <ComingSoon
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        feature={comingSoonFeature}
      />
    </Container>
  );
};

export default VoteResults;
